/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  KeyValue,
  useGetAccountsQuery,
  DiscoveryRule,
} from "~/generated/graphql";
import { isNotNil, isEmpty, isNotEmpty } from "~/tools";
import {
  DiscoveryRuleMatchKeys,
  DiscoveryRuleInput,
  SelectorOption,
} from "../types";
import { getCloudAccounts } from "../utils";
import { mergeMatches } from "./utils";

type FormState = {
  discoveryRules: DiscoveryRule[];
  addRule: (input: DiscoveryRuleInput) => void;
  updateRule: (id: string, input: DiscoveryRuleInput) => void;
  deleteRule: (
    id: string,
    ruleType: DiscoveryRuleMatchKeys,
    ruleValue?: KeyValue | string
  ) => void;
  isLoading: boolean;
  accounts: SelectorOption[];
  deleteEmptyRule: (ruleId: string) => void;
};

const initialState = {
  discoveryRules: [],
  addRule: () => undefined,
  updateRule: () => undefined,
  deleteRule: () => undefined,
  isLoading: false,
  accounts: [],
  deleteEmptyRule: () => undefined,
};

export const FormContext = React.createContext<FormState>(initialState);
FormContext.displayName = "DiscoveryRulesFormContext";

type Props = {
  children: React.ReactNode;
  initialRules: DiscoveryRule[];
};

function ruleHasMatches(rule: DiscoveryRule) {
  return Object.values(rule.match).some(
    (value) => isNotNil(value) && isNotEmpty(value)
  );
}

export function FormContextProvider({ children, initialRules }: Props) {
  const [discoveryRules, setDiscoveryRules] =
    React.useState<DiscoveryRule[]>(initialRules);

  React.useEffect(() => {
    setDiscoveryRules(initialRules);
  }, [initialRules]);

  const { data, loading } = useGetAccountsQuery();

  const addRule = (input: DiscoveryRuleInput) => {
    setDiscoveryRules((prevState) => [
      ...prevState,
      {
        id: uuidv4(),
        name: `DR${Date.now().toString()}`,
        ...(input as Omit<DiscoveryRule, "id" | "name">),
      },
    ]);
  };

  const updateRule = useCallback(
    (id: string, input: DiscoveryRuleInput) => {
      const isExistingRule = discoveryRules.some((rule) => rule.id === id);

      if (!isExistingRule) {
        return;
      }

      setDiscoveryRules((prevState) =>
        prevState.map((rule) => {
          if (rule.id === id) {
            return {
              ...rule,
              ...(input.match != null && {
                match: mergeMatches(rule.match, input.match),
              }),
              ...(input.exclusive != null && {
                exclusive: input.exclusive,
              }),
              ...(input.ibmResourceGroup != null && {
                ibmResourceGroup: input.ibmResourceGroup,
              }),
            };
          }
          return rule;
        })
      );
    },
    [discoveryRules]
  );

  const deleteRule = (
    id: string,
    matchType: DiscoveryRuleMatchKeys,
    ruleValue?: KeyValue | string
  ) => {
    setDiscoveryRules((prevState) =>
      prevState
        .map((rule) => {
          if (rule.id !== id) {
            return rule;
          }

          if (ruleValue != null) {
            return {
              ...rule,
              match: {
                ...rule.match,
                [matchType]: (
                  rule.match[matchType] as (KeyValue | string)[]
                ).filter((value: KeyValue | string) => value !== ruleValue),
              },
            };
          }

          const { [matchType]: ignoredMatchType, ...restMatch } = rule.match;

          // The rule is empty (it has only id field) so it can be removed
          const isRuleEmpty =
            isEmpty(Object.keys(restMatch)) ||
            Object.values(restMatch).every(
              (value) => value == null || isEmpty(value)
            );

          if (isRuleEmpty) {
            return null;
          }

          return {
            ...rule,
            match: restMatch,
          };
        })
        .filter(isNotNil)
        .filter(ruleHasMatches)
    );
  };

  const deleteEmptyRule = useCallback(
    (ruleId: string) => {
      if (discoveryRules.length > 1) {
        setDiscoveryRules(discoveryRules.filter(({ id }) => id !== ruleId));
      }
    },
    [discoveryRules]
  );

  const value = useMemo(
    () => ({
      discoveryRules,
      addRule,
      updateRule,
      deleteRule,
      isLoading: loading,
      accounts: getCloudAccounts(data?.accounts?.filter(isNotNil) ?? []),
      deleteEmptyRule,
    }),
    [data?.accounts, deleteEmptyRule, discoveryRules, loading, updateRule]
  );

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}
